<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue'
import { useStore } from 'vuex'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import { errorNotify, successNotify } from '@/mixins'

// The cell's board: its CAN address (read-only — the device assigns it) and the board
// replacement. Replacing only drops the board's confirmation; the device in maintenance
// hands the new board a fresh address, and board_confirmed flips back once it reports in.
const props = defineProps({
  cell: { type: Object, required: true },
  canReplace: { type: Boolean, default: false },
  inMaintenance: { type: Boolean, default: false },
})

const emit = defineEmits(['changed', 'maintenance'])
const store = useStore()

const confirmOpen = ref(false)
const busy = ref(false)

const showReplace = computed(() => props.canReplace && props.cell.board_confirmed)
const confirmMessage = computed(() =>
  `The board address of cell ${props.cell.number} will be reset. Once the board is swapped, `
  + 'the device hands it a new address. The cell content and its order stay as they are.')

function replaceBoard() {
  confirmOpen.value = false
  busy.value = true
  store.dispatch('cell/replaceCellBoard', props.cell.id)
    .then(() => {
      const error = store.getters['cell/cellReplaceBoardError']
      if (error) {
        errorNotify(error)
        return
      }
      successNotify(`Cell ${props.cell.number}: waiting for the new board`)
      emit('changed')
    })
    .finally(() => { busy.value = false })
}
</script>

<template>
  <div class="cboard">
    <div class="cboard__row">
      <span class="mdi mdi-chip cboard__ico"></span>
      <div class="cboard__main">
        <span class="cboard__label">Board address</span>
        <span class="mono cboard__addr">{{ cell.address ?? '—' }}</span>
      </div>
      <span v-if="!cell.board_confirmed" class="pill pill--maint cboard__pill">
        <span class="mdi mdi-timer-sand"></span> Awaiting new board
      </span>
      <span
        v-else-if="showReplace"
        class="cboard__btnwrap"
        :title="inMaintenance ? '' : 'Put the device into maintenance mode'">
        <button
          class="btn-ghost cboard__btn"
          type="button"
          :disabled="!inMaintenance || busy"
          @click="confirmOpen = true">
          <span class="mdi" :class="busy ? 'mdi-loading mdi-spin' : 'mdi-swap-horizontal'"></span>
          Replace board
        </button>
      </span>
    </div>

    <div v-if="!cell.board_confirmed" class="cboard__hint">
      The device gives the new board an address in maintenance mode. This page refreshes
      until the board confirms it.
    </div>
    <div v-else-if="showReplace && !inMaintenance" class="cboard__hint">
      Put the device into maintenance mode to replace the board.
      <button class="cboard__link" type="button" @click="emit('maintenance')">Switch to maintenance</button>
    </div>

    <ConfirmModal
      :open="confirmOpen"
      :title="`Replace the board of cell ${cell.number}?`"
      :message="confirmMessage"
      confirm-label="Replace board"
      icon="swap-horizontal"
      @confirm="replaceBoard"
      @cancel="confirmOpen = false" />
  </div>
</template>
